import type { ContentNode, DocumentState } from '../document-types';
import type { NodeHandler, RenderContext } from './types';

export const INPUT_SHAPE_NODE_TYPE = 'input-shape';

/**
 * Primitives only. Nested objects/arrays are deliberately not offered: the
 * input becomes a tool-call schema and a run form, and both stay simple when
 * every field is a single value.
 */
export const INPUT_SHAPE_FIELD_TYPES = ['string', 'number', 'boolean'] as const;

export type InputShapeFieldType = (typeof INPUT_SHAPE_FIELD_TYPES)[number];

export type InputShapeField = {
  readonly name: string;
  readonly type: InputShapeFieldType;
  readonly description: string | null;
};

export const INPUT_SHAPE_KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

const isFieldType = (value: unknown): value is InputShapeFieldType =>
  typeof value === 'string' &&
  (INPUT_SHAPE_FIELD_TYPES as readonly string[]).includes(value);

/**
 * Reads the `fields` attr of an `input-shape` node. Rows are dropped rather
 * than failing the whole block: a half-typed name or a duplicate is normal
 * while someone is editing, and Coco can author these attrs too.
 */
export function parseInputShapeFields(
  attrs: Record<string, unknown> | null | undefined
): InputShapeField[] {
  const raw = attrs?.['fields'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const out: InputShapeField[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const { name, type, description } = entry as Record<string, unknown>;
    if (typeof name !== 'string' || !isFieldType(type)) {
      continue;
    }
    const trimmed = name.trim();
    if (!INPUT_SHAPE_KEY_PATTERN.test(trimmed) || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    out.push({
      name: trimmed,
      type,
      description:
        typeof description === 'string' && description.trim() !== ''
          ? description.trim()
          : null,
    });
  }
  return out;
}

// Rendered where it sits rather than hoisted: `top` would land above the
// agent's title heading.
export class InputShapeHandler implements NodeHandler {
  readonly type = INPUT_SHAPE_NODE_TYPE;

  render(
    node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    _childContent: string,
    _ctx: RenderContext
  ): string {
    const fields = parseInputShapeFields((node as ContentNode).attrs);
    if (fields.length === 0) {
      return '';
    }
    const lines = fields.map((field) =>
      field.description === null
        ? `- \`${field.name}\` (${field.type})`
        : `- \`${field.name}\` (${field.type}): ${field.description}`
    );
    return `**You are given these inputs as JSON:**\n${lines.join('\n')}\n\n`;
  }
}
